import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PathsUrls } from '../../utils/path_url';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import rubaaiLogo from '../../assets/logo/rubaai_logo.png';

const menuItems = [
  { label: 'Home', route: PathsUrls.home_v2 },
  { label: 'About', route: PathsUrls.about },
  { label: 'Our Team', route: PathsUrls.ourTeam },
  { label: 'Careers', route: PathsUrls.careers || '/careers' },
  { label: 'Contact Us', route: PathsUrls.contactUs || '/contact-us' },
];

const MobileAppBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const handleMenuToggle = () => {
    setMenuOpen(!menuOpen);
  };

  const handleNavigation = (route) => {
    navigate(route);
    setMenuOpen(false);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the menu and not on the menu icon
      if (menuOpen && menuRef.current && !menuRef.current.contains(event.target)) {
        // Check if the click is not on the menu icon (hamburger button)
        const menuIcon = event.target.closest('[aria-label="menu"]');
        if (!menuIcon) {
          setMenuOpen(false);
        }
      }
    };

    if (menuOpen) {
      // Add a small delay to prevent immediate closure when opening menu
      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('click', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }
  }, [menuOpen]);

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          background: '#000000',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid #000000',
          boxShadow: 'none',
          width: '100%',
          maxWidth: '100vw',
          left: 0,
          right: 0,
          overflowX: 'hidden',
        }}
      >
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            padding: '8px 16px',
            minHeight: '56px',
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
          }}
        >
          {/* Logo */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            maxWidth: '60%',
          }}>
            <img
              src={rubaaiLogo}
              alt="Rubaai Logo"
              style={{
                height: '60px',
                width: 'auto',
                maxWidth: '100%',
              }}
            />
          </Box>

          {/* Hamburger Menu */}
          <IconButton
            edge="end"
            color="inherit"
            aria-label="menu"
            onClick={handleMenuToggle}
            sx={{
              color: '#fff',
              flexShrink: 0,
              marginLeft: 'auto',
              '&:hover': {
                backgroundColor: 'rgba(191, 158, 254, 0.1)',
              },
            }}
          >
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Backdrop to close menu when clicking outside */}
      {menuOpen && (
        <Box
          onClick={() => setMenuOpen(false)}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 40,
            background: 'transparent',
          }}
        />
      )}

      {/* Mobile Navigation Dropdown */}
      {menuOpen && (
        <Box
          ref={menuRef}
          onClick={(e) => e.stopPropagation()}
          sx={{
            position: 'fixed',
            top: '80px',
            right: '16px',
            background: 'rgba(0, 0, 0, 1)',
            border: '1px solid rgba(191, 158, 254, 1)',
            borderRadius: '15px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
            display: 'flex',
            flexDirection: 'column',
            width: '160px',
            zIndex: 50,
            animation: 'fadeIn 0.2s ease-in-out',
            backdropFilter: 'blur(10px)',
            '@keyframes fadeIn': {
              '0%': {
                opacity: 0,
                transform: 'translateY(-10px)',
              },
              '100%': {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          }}
        >
          {menuItems.map((item, index) => (
            <React.Fragment key={item.label}>
              <Box
                component="button"
                onClick={() => handleNavigation(item.route)}
                sx={{
                  px: 3,
                  py: 1.5,
                  color: location.pathname === item.route
                    ? 'rgba(191, 158, 254, 1)'
                    : '#fff',
                  fontWeight: 400,
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: '16px',
                  fontFamily: 'Wix Madefor Display, sans-serif',
                  transition: 'all 0.2s ease-in-out',
                  width: '100%',
                  '&:first-of-type': {
                    borderTopLeftRadius: '16px',
                    borderTopRightRadius: '16px',
                  },
                  ...(location.pathname === item.route && {
                    // backgroundColor: 'rgba(0, 0, 0, 0.1)',
                    color: 'rgba(191, 158, 254, 1)',
                  }),
                }}
              >
                {item.label}
              </Box>
              {index < menuItems.length - 1 && (
                <Box
                  sx={{
                    margin: '0 auto',
                    px: '5px',
                    height: '1px',
                    background: 'linear-gradient(to right, rgba(119, 119, 119, 1), rgba(24, 23, 26, 1))',
                    width: '90%',
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </Box>
      )}

      {/* Spacer to prevent content from being hidden behind the appbar */}
      <Toolbar />
    </>
  );
};

export default MobileAppBar; 